import Datepicker from '@/components_v2/DatePicker';
/* eslint-disable no-restricted-syntax */
import { useFilter } from '@/hooks/Filters/filter';
import { Text } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { DateFilterProps } from './interfaces';

const DateFilter: React.FC<DateFilterProps> = ({
  column,
  type,
  table,
  intervalDays,
}) => {
  const { selectOption, getSelectedOptions } = useFilter();
  const [minDate, setMinDate] = useState<Date | undefined>(undefined);
  const [maxDate, setMaxDate] = useState<Date | undefined>(undefined);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  const getValue = useCallback(
    (current?: Date[]) => {
      const selected = getSelectedOptions({ column, type, table });
      if (selected) {
        if (selected.type === 'date') {
          if (!current || selected.selected !== current[0]) {
            return [selected.selected, selected.selected];
          }
        }
        if (selected.type === 'date_range') {
          if (
            !current ||
            selected.selected.dateStart !== current[0] ||
            selected.selected.dateEnd !== current[1]
          ) {
            return [selected.selected.dateStart, selected.selected.dateEnd];
          }
        }
      }
      return undefined;
    },
    [column, getSelectedOptions, type, table],
  );

  const [selectedDate, setSelectedDate] = useState<Date[] | undefined>(() =>
    getValue(),
  );

  useEffect(() => {
    const newDifferentValue = getValue(selectedDate);
    if (newDifferentValue) {
      setSelectedDate(newDifferentValue);
    }
  }, [selectedDate, getValue]);

  const onChangeDate = useCallback(
    (newDate: Date[]) => {
      if (type === 'date_range') {
        setSelectedDate(newDate);
        selectOption({
          table,
          column,
          type,
          selected: { dateStart: newDate[0], dateEnd: newDate[1] },
        });
      }
      if (type === 'date') {
        setSelectedDate(newDate);
        selectOption({
          table,
          column,
          type,
          selected: newDate[0],
        });
      }
    },
    [type, selectOption, column, table],
  );

  const handleClickDay = useCallback(
    (date: Date) => {
      if (type !== 'date_range' || !intervalDays) return;
      if (!isSelectingRange) {
        const upperLimit = new Date(date);
        upperLimit.setDate(upperLimit.getDate() + (intervalDays.max - 1));
        const lowerLimit = new Date(date);
        lowerLimit.setDate(lowerLimit.getDate() - (intervalDays.max - 1));
        setMinDate(lowerLimit);
        setMaxDate(upperLimit);
        setIsSelectingRange(true);
      } else {
        setMinDate(undefined);
        setMaxDate(undefined);
        setIsSelectingRange(false);
      }
    },
    [type, intervalDays, isSelectingRange],
  );

  return (
    <>
      {type === 'date_range' && intervalDays?.description && (
        <Text fontStyle="italic" color="gray.500" fontSize="sm" px={2}>
          {intervalDays.description}
        </Text>
      )}
      <Datepicker
        type={type}
        selectedDate={selectedDate}
        onChangeDate={onChangeDate}
        minDate={minDate}
        maxDate={maxDate}
        onClickDay={handleClickDay}
      />
    </>
  );
};

export default DateFilter;
